import { NgModule } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule, DatePipe } from '@angular/common';
import {
  DataTableModule, MenuModule, MenubarModule, SidebarModule, CalendarModule, DialogModule, InputSwitchModule, TooltipModule,
  InputTextareaModule, DropdownModule, ToggleButtonModule, MultiSelectModule, TabViewModule, ChipsModule, FileUploadModule,
  AccordionModule, PanelModule, RadioButtonModule, InputTextModule
} from 'primeng/primeng';

import { TableModule } from 'primeng/table';

import { FroalaEditorModule, FroalaViewModule } from 'angular-froala-wysiwyg';

import { AppHeaderModule } from '../app-header/app-header.module';
import { AppSubHeaderModule } from '../app-sub-header/app-sub-header.module';
import { AppErrorMsgModule } from '../app-error-msg/app-error-msg.module';
import { AppBannerMsgModule } from '../app-banner-msg/app-banner-msg.module';
import { AppPushAsideContentModule } from '../app-push-aside-content/app-push-aside-content.module';
import { AppLeftNavModule } from '../app-left-nav/app-left-nav.module';
import { AppLoadingPanelModule } from '../app-loading-panel/app-loading-panel.module';
import { StrategiesRoutingModule } from './strategies-routing.module';

// resolves
import { StrategiesListResolve } from './services/strategies-list-resolve.service';
import { StrategyIdResolve } from './services/strategy-id-resolve.service';
import { PlanTreeResolve } from './services/plan-tree-resolve.service';
import { PurposeMissionBeliefsResolve } from './services/purpose-mission-beliefs-resolve.service';
import { PlanCoordinateResolve } from './services/plan-coordinate-resolve.service';
import { CustomerChildrenResolveService } from './customer-children-resolve.service';

// services
import { StrategiesPermissionsService } from './services/strategies-permissions.service';
import { StrategiesService } from './services/strategies.service';
import { PlanService } from './services/plan.service';
import { StrategyService } from './services/strategy.service';
import { PriorityService } from './services/priority.service';
import { PriorityComponent } from './plan/envisioning/priority/priority.component';
import { TagService } from './services/tag.service';
import { ObjectiveService } from './services/objective.service';
import { UserMgmtService } from '../user-mgmt/user-mgmt.service';
import { KeyMeasureService } from './services/key-measure.service';
import { OutcomeService } from './services/outcome.service';
import { DeliverableService } from './services/deliverable.service';
import { StrategyMapDownloadService } from './strategy-map-download/strategy-map-download.service';
import { ActivityService } from './services/activity.service';
import { ImplementationPlanService } from './services/implementation-plan.service';
import { ResourceService } from './services/resource.service';
import { DeepLinkGuardService } from '../auth/services/deep-link-guard.service';

// common
import { ProgressImgDirective } from './progress-img.directive';
import { RichContentComponent } from './plan/rich-content/rich-content.component';
import { TableCellPipe } from './plan/deliverables/theory-of-action/table-cell.pipe';

// Strategies components
import { StrategiesComponent } from './strategies.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { StrategyDetailsComponent } from './dashboard/strategy-details/strategy-details.component';
import { PlanTreeComponent } from './plan/plan-tree/plan-tree.component';
import { PlanComponent } from './plan/plan.component';

// Envisioning components
// import { EnvisioningComponent } from './plan/envisioning/envisioning.component';
import { DetermineCurrentRealityComponent } from './plan/envisioning/determine-current-reality/determine-current-reality.component';
import { ExploreTheFutureComponent } from './plan/envisioning/explore-the-future/explore-the-future.component';
import { SynthesizeResultsComponent } from './plan/envisioning/synthesize-results/synthesize-results.component';


// Planning components
import {
  CreatingObjectivesCriticalInitiativesComponent
} from './plan/planning/creating-objectives-critical-initiatives/creating-objectives-critical-initiatives.component';
import {
  IdentifyingOutcomesKeyMeasuresComponent
} from './plan/planning/identifying-outcomes-key-measures/identifying-outcomes-key-measures.component';
import { KeyMeasureComponent } from './plan/planning/identifying-outcomes-key-measures/key-measure/key-measure.component';
import { OutcomeComponent } from './plan/planning/identifying-outcomes-key-measures/outcome/outcome.component';
import { DevelopAnnualPlanComponent } from './plan/planning/develop-annual-plan/develop-annual-plan.component';
import { DevelopStrategyMapComponent } from './plan/planning/develop-strategy-map/develop-strategy-map.component';
import { ObjectiveComponent } from './plan/planning/creating-objectives-critical-initiatives/objective/objective.component';
import { InitiativeComponent } from './plan/planning/creating-objectives-critical-initiatives/initiative/initiative.component';

// Implementing components
import { DesignImplementationPlanComponent } from './plan/implementing/design-implementation-plan/design-implementation-plan.component';
import { ActivityComponent } from './plan/implementing/design-implementation-plan/activity/activity.component';
import { EnactMonitorProgressComponent } from './plan/implementing/enact-monitor-progress/enact-monitor-progress.component';
import { InitiativeService } from './services/initiative.service';

// Evaluating components
import { OutcomeImpactComponent } from './plan/evaluating/outcome-impact/outcome-impact.component';

// Outputs components
import { DeliverablesComponent } from './plan/deliverables/deliverables.component';
import { StrategyMapComponent } from './plan/deliverables/strategy-map/strategy-map.component';
import { AnnualPlanComponent } from './plan/deliverables/annual-plan/annual-plan.component';
import { ImplementationPlanComponent } from './plan/deliverables/implementation-plan/implementation-plan.component';
import { TheoryOfActionComponent } from './plan/deliverables/theory-of-action/theory-of-action.component';
import { PriorityHeadingsComponent } from './plan/deliverables/priority-headings/priority-headings.component';
import { EditOverlayComponent } from './plan/deliverables/edit-overlay/edit-overlay.component';
import { PriorityStackHeaderComponent } from './plan/deliverables/strategy-map/priority-stack-header/priority-stack-header.component';
import { PriorityStackSectionComponent } from './plan/deliverables/strategy-map/priority-stack-section/priority-stack-section.component';
import { InputNumberDirective } from './input-number.directive';

// Strategy map download components
import { StrategyMapDownloadComponent } from './strategy-map-download/strategy-map-download.component';
import { StrategyMapFormComponent } from './strategy-map-download/strategy-map-form/strategy-map-form.component';
import { StrategyMapRecipientsComponent } from './strategy-map-download/strategy-map-recipients/strategy-map-recipients.component';
import { StrategyMapConfirmComponent } from './strategy-map-download/strategy-map-confirm/strategy-map-confirm.component';
import { WorkspaceService } from './services/workspace.service';
import { CategoryInformationComponent } from './plan/category-information/category-information.component';
import {AttachmentUploadModalComponent} from '../shared/components/attachment-upload-modal/attachment-upload-modal.component';
import {SharedModule} from '../shared/shared.module';
import { EnvisioningTableComponent } from './plan/envisioning-table/envisioning-table.component';
import {AppLoadingService} from '../app-loading-panel/app-loading.service';
import { JudgeSuccessComponent } from './plan/evaluating/judge-success/judge-success.component';
import {AssumptionFlyoutComponent} from './plan/planning/identifying-outcomes-key-measures/assumption-flyout/assumption-flyout.component';
import {InfluentialFactorFlyoutComponent} from './plan/planning/identifying-outcomes-key-measures/influential-factor-flyout/influential-factor-flyout.component';

@NgModule({
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    FroalaEditorModule.forRoot(),
    FroalaViewModule.forRoot(),
    DataTableModule,
    TableModule,
    AccordionModule,
    PanelModule,
    MenuModule,
    MenubarModule,
    SidebarModule,
    CalendarModule,
    DialogModule,
    RadioButtonModule,
    TabViewModule,
    AppHeaderModule,
    AppSubHeaderModule,
    StrategiesRoutingModule,
    AppErrorMsgModule,
    AppBannerMsgModule,
    InputSwitchModule,
    InputTextareaModule,
    InputTextModule,
    TooltipModule,
    DropdownModule,
    MultiSelectModule,
    TabViewModule,
    ChipsModule,
    ToggleButtonModule,
    FileUploadModule,
    AppPushAsideContentModule,
    AppLeftNavModule,
    AppLoadingPanelModule,
    SharedModule
  ],
  bootstrap: [ StrategiesComponent ],
  providers: [
    DatePipe,
    AppLoadingService,
    StrategiesListResolve,
    PurposeMissionBeliefsResolve,
    StrategyIdResolve,
    PlanTreeResolve,
    PlanCoordinateResolve,
    StrategiesPermissionsService,
    StrategiesService,
    StrategyService,
    PlanService,
    PriorityService,
    InitiativeService,
    ObjectiveService,
    OutcomeService,
    KeyMeasureService,
    TagService,
    UserMgmtService,
    DeliverableService,
    ActivityService,
    ImplementationPlanService,
    ResourceService,
    StrategyMapDownloadService,
    WorkspaceService,
    DeepLinkGuardService,
    CustomerChildrenResolveService
  ],
  declarations: [
    // common
    ProgressImgDirective,
    RichContentComponent,
    TableCellPipe,

    // strategies
    DashboardComponent,
    StrategiesComponent,
    StrategyDetailsComponent,
    PlanTreeComponent,
    PlanComponent,

    // Envisioning
    // EnvisioningComponent,
    PriorityComponent,
    DetermineCurrentRealityComponent,
    ExploreTheFutureComponent,
    SynthesizeResultsComponent,

    // Planning
    CreatingObjectivesCriticalInitiativesComponent,
    IdentifyingOutcomesKeyMeasuresComponent,
    OutcomeComponent,
    KeyMeasureComponent,
    DevelopAnnualPlanComponent,
    DevelopStrategyMapComponent,
    ObjectiveComponent,
    InitiativeComponent,
    AssumptionFlyoutComponent,
    InfluentialFactorFlyoutComponent,

    // Implementing
    DesignImplementationPlanComponent,
    ActivityComponent,
    EnactMonitorProgressComponent,

    // Evaluating
    OutcomeImpactComponent,

    // Outputs
    DeliverablesComponent,
    StrategyMapComponent,
    AnnualPlanComponent,
    ImplementationPlanComponent,
    TheoryOfActionComponent,
    PriorityHeadingsComponent,
    EditOverlayComponent,
    PriorityStackHeaderComponent,
    PriorityStackSectionComponent,
    InputNumberDirective,

    // Strategy map download
    StrategyMapDownloadComponent,
    StrategyMapFormComponent,
    StrategyMapRecipientsComponent,
    StrategyMapConfirmComponent,
    CategoryInformationComponent,

    AttachmentUploadModalComponent,

    EnvisioningTableComponent,

    JudgeSuccessComponent

  ]
})

export class StrategiesModule { }
